import { API_ENDPOINTS } from "../constants";
import { ImageWithMetadata } from "../constants/inspectionPhotos";

export function getThumbAssetId(assetId: string): string {
    return `${assetId}-thumb`;
}

export function getPreviewAssetId(assetId: string): string {
    return `${assetId}-preview`;
}

export function resolveThumbAssetId(
    assetId: string,
    thumbAssetId?: string | null,
): string {
    return thumbAssetId || getThumbAssetId(assetId);
}

export function resolvePreviewAssetId(
    assetId: string,
    previewAssetId?: string | null,
): string {
    return previewAssetId || getPreviewAssetId(assetId);
}

export interface TaskListAsset {
    AssetId: string;
    Label: string;
    ThumbAssetId?: string | null;
    PreviewAssetId?: string | null;
    Note?: string | null;
}

export function mapTaskAssetToImage(asset: TaskListAsset): ImageWithMetadata {
    const assetId = asset.AssetId;
    const thumbAssetId = resolveThumbAssetId(assetId, asset.ThumbAssetId);
    const previewAssetId = resolvePreviewAssetId(
        assetId,
        asset.PreviewAssetId,
    );

    return {
        assetId,
        thumbAssetId,
        previewAssetId,
        url: API_ENDPOINTS.asset.getImageAsSrc(thumbAssetId),
        note: asset.Note ?? null,
    };
}

export function mapUploadResponseToImage(
    response: {
        AssetId: string;
        ThumbAssetId?: string | null;
        PreviewAssetId?: string | null;
        Label?: string;
    },
    note?: string | null,
): ImageWithMetadata {
    return mapTaskAssetToImage({
        AssetId: response.AssetId,
        Label: response.Label ?? "",
        ThumbAssetId: response.ThumbAssetId,
        PreviewAssetId: response.PreviewAssetId,
        Note: note ?? null,
    });
}

export function isBlobImageUrl(url?: string | null): boolean {
    return !!url && url.startsWith("blob:");
}

export function isPendingUpload(image: ImageWithMetadata): boolean {
    return isBlobImageUrl(image.url) && !image.assetId;
}

export function getDisplayAssetId(
    image: ImageWithMetadata,
): string | undefined {
    if (isBlobImageUrl(image.url)) {
        return undefined;
    }

    return image.thumbAssetId ?? image.assetId;
}

export function getDisplayUrl(image: ImageWithMetadata): string | undefined {
    return isBlobImageUrl(image.url) ? image.url : undefined;
}

/** Full-screen / modal viewer — prefers Full HD preview variant over original. */
export function toPreviewImage(image: ImageWithMetadata): ImageWithMetadata {
    if (isBlobImageUrl(image.url)) {
        return {
            ...image,
            url: image.url,
        };
    }

    return {
        ...image,
        url: "",
        assetId:
            image.previewAssetId ||
            (image.assetId ? getPreviewAssetId(image.assetId) : image.assetId),
    };
}
